import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../services/auth.service';

interface LogEntry { t: string; level: string; msg: string; }
interface NetEntry { t: string; method: string; url: string; status: number; ms: number; }
interface ErrEntry { t: string; type: string; msg: string; stack?: string; }
interface NavEntry { t: string; url: string; }

const CAP = { console: 120, net: 60, err: 40, nav: 40 };

/**
 * Recolector de diagnóstico para "Reportar un problema": mantiene buffers en
 * memoria (consola, red, errores, navegación) y arma un snapshot al reportar.
 * Se instala una vez al arranque de la app.
 */
@Injectable({ providedIn: 'root' })
export class DiagnosticsService {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  private logs: LogEntry[] = [];
  private nets: NetEntry[] = [];
  private errs: ErrEntry[] = [];
  private navs: NavEntry[] = [];
  private installed = false;

  install(): void {
    if (this.installed || typeof window === 'undefined') return;
    this.installed = true;

    // Consola
    (['log', 'info', 'warn', 'error'] as const).forEach((level) => {
      const orig = (console as any)[level]?.bind(console);
      if (!orig) return;
      (console as any)[level] = (...args: unknown[]) => {
        try {
          this._push(this.logs, CAP.console, { t: this._now(), level, msg: this._fmt(args) });
        } catch { /* noop */ }
        orig(...args);
      };
    });

    // Errores no controlados
    window.addEventListener('error', (e: ErrorEvent) => {
      this._push(this.errs, CAP.err, {
        t: this._now(), type: 'error', msg: this._redact(e.message || 'Error'),
        stack: e.error?.stack ? this._redact(String(e.error.stack)).slice(0, 1500) : undefined,
      });
    });
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      const reason: any = e.reason;
      this._push(this.errs, CAP.err, {
        t: this._now(), type: 'unhandledrejection',
        msg: this._redact(reason?.message ? String(reason.message) : String(reason)).slice(0, 500),
        stack: reason?.stack ? this._redact(String(reason.stack)).slice(0, 1500) : undefined,
      });
    });

    // Navegación
    this.router.events.subscribe((ev) => {
      if (ev instanceof NavigationEnd) {
        this._push(this.navs, CAP.nav, { t: this._now(), url: ev.urlAfterRedirects });
      }
    });
  }

  /** Registra una llamada de red (lo invoca el interceptor). */
  recordNetwork(method: string, url: string, status: number, ms: number): void {
    this._push(this.nets, CAP.net, {
      t: this._now(), method, url: this._stripQuery(url), status, ms: Math.round(ms),
    });
  }

  /** Arma el snapshot de diagnóstico (JSON serializado, con datos redactados). */
  snapshot(): string {
    const token = this.auth.decodeToken?.() as any;
    const snap = {
      generatedAt: new Date().toISOString(),
      context: {
        url: window.location.href,
        route: this.router.url,
        businessNameId: token?.businessNameId ?? null,
        user: token ? { email: token.email, username: token.username, roles: token.roles } : null,
        userAgent: navigator.userAgent,
        platform: (navigator as any).userAgentData?.platform ?? navigator.platform,
        language: navigator.language,
        viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
      },
      navigation: this.navs.slice(-CAP.nav),
      console: this.logs.slice(-CAP.console),
      network: this.nets.slice(-CAP.net),
      errors: this.errs.slice(-CAP.err),
    };
    try {
      return JSON.stringify(snap, null, 2);
    } catch {
      return JSON.stringify({ error: 'No se pudo serializar el diagnóstico', context: snap.context });
    }
  }

  // ── Internos ────────────────────────────────────────────────────────────
  private _now(): string { return new Date().toISOString(); }
  private _push<T>(arr: T[], cap: number, item: T): void {
    arr.push(item);
    if (arr.length > cap) arr.splice(0, arr.length - cap);
  }
  private _fmt(args: unknown[]): string {
    return this._redact(
      args.map((a) => {
        if (typeof a === 'string') return a;
        try { return JSON.stringify(a); } catch { return String(a); }
      }).join(' '),
    ).slice(0, 800);
  }
  private _stripQuery(url: string): string {
    const i = url.indexOf('?');
    return i >= 0 ? url.slice(0, i) : url;
  }
  /** Redacta tokens/valores sensibles evidentes. */
  private _redact(s: string): string {
    return s
      .replace(/(Bearer\s+)[A-Za-z0-9._-]+/gi, '$1[redactado]')
      .replace(/(eyJ[A-Za-z0-9._-]{20,})/g, '[jwt-redactado]')
      .replace(/("?(?:password|token|authorization|secret|apiKey)"?\s*[:=]\s*)"?[^",}\s]+/gi, '$1[redactado]');
  }
}
